import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Container, Paper, Stack, Typography, Box, Divider,
  Button, TextField, Alert, CircularProgress, Link, Backdrop
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { 
  sendEmailVerification,
  sendSignInLinkToEmail,
  signInWithPopup,
  signOut,
  setPersistence,
  browserLocalPersistence,
  Auth,
  User
} from "firebase/auth";
import { auth as defaultAuth, provider } from '../firebase';
import { logEvent } from '../analytics';

interface LoginPageProps {
  authInstance?: Auth;
  onSignInWithGoogle?: () => void;
  onLoginSuccess?: () => void;
  initialUnverifiedUser?: User | null;
}

const GoogleIcon = () => (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ width: 20, height: 20 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
    <path fill="none" d="M0 0h48v48H0z"></path>
  </svg>
);

const getFirebaseErrorMessage = (error: any): string => {
  const code = error?.code;
  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'The sign-in link is invalid or has expired. Please request a new link.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for authentication in Firebase Console.';
    default:
      return error?.message || 'Authentication failed. Please try again.';
  }
};

const LoginPage: React.FC<LoginPageProps> = ({ 
  authInstance, 
  onSignInWithGoogle, 
  onLoginSuccess, 
  initialUnverifiedUser 
}) => {
  const auth = authInstance || defaultAuth;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [unverifiedUser, setUnverifiedUser] = useState<User | null>(initialUnverifiedUser || null);

  React.useEffect(() => {
    document.title = "Jets '14 | Login";
  }, []);

  const handleAuthResult = async (user: User) => {
    setLoading(true);
    try {
      await user.reload();
      if (!user.emailVerified) {
        setLoading(false);
        setUnverifiedUser(user);
        return;
      }
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (e) {
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handlePasswordlessSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email || !email.trim()) {
      setError('Please enter your email address to receive a sign-in link.');
      return;
    }

    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const actionCodeSettings = {
        url: window.location.origin + '/auth/action',
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, trimmedEmail, actionCodeSettings);
      try {
        window.localStorage.setItem('emailForSignIn', trimmedEmail);
      } catch (storageErr) {
        console.warn('Could not save email to localStorage:', storageErr);
      }
      logEvent('login', { method: 'Email_Link' });
      setMessage(`Sign-in link sent to ${trimmedEmail}! Check your email inbox and click the link to sign in.`);
    } catch (err: any) {
      console.error("Passwordless sign-in error:", err);
      setError(getFirebaseErrorMessage(err));
      logEvent('login_error', { method: 'Email_Link', error: err?.code });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (onSignInWithGoogle && !authInstance) {
      onSignInWithGoogle();
      return;
    }

    setError(null);
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithPopup(auth, provider);
      logEvent('login', { method: 'Google' });
      await handleAuthResult(userCredential.user);
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      setError(getFirebaseErrorMessage(err));
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await sendEmailVerification(unverifiedUser);
      setMessage(`Verification email resent to ${unverifiedUser.email}! Please check your inbox.`);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCheck = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await unverifiedUser.reload();
      if (unverifiedUser.emailVerified) {
        setUnverifiedUser(null);
        await handleAuthResult(unverifiedUser);
      } else {
        setLoading(false);
        setError('Email is still not verified. Please check your inbox and click the verification link.');
      }
    } catch (err: any) {
      setLoading(false);
      setError('Failed to check verification status. Please try again.');
    }
  };

  const handleCancelVerification = async () => {
    await signOut(auth);
    setUnverifiedUser(null);
    setError(null);
    setMessage(null);
  };

  // Verification Screen
  if (unverifiedUser) {
    return (
      <Container component="main" maxWidth="xs">
        <Paper 
          elevation={3}
          sx={{
            marginTop: { xs: 3, sm: 6 },
            marginBottom: { xs: 3, sm: 6 },
            padding: { xs: 2.5, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1.5, borderRadius: '50%', mb: 2 }}>
            <MarkEmailReadIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography component="h1" variant="h5" textAlign="center" fontWeight="bold" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mb: 2, color: 'text.secondary' }}>
            We've sent a verification link to <strong>{unverifiedUser.email}</strong>. 
            Please click the link in that email to verify your account before accessing the team portal.
          </Typography>
          
          {error && <Alert severity="error" sx={{ mt: 1, mb: 2, width: '100%' }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ mt: 1, mb: 2, width: '100%' }}>{message}</Alert>}

          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleVerificationCheck}
            disabled={loading}
            sx={{ mt: 2, mb: 1, py: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : "I've verified my email"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendVerification}
            disabled={loading}
            sx={{ mb: 1, py: 0.8 }}
          >
            Resend Verification Email
          </Button>

          <Button
            fullWidth
            onClick={handleCancelVerification}
            color="inherit"
            size="small"
            sx={{ mt: 1 }}
          >
            Back to Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3}
        sx={{
          marginTop: { xs: 3, sm: 6 },
          marginBottom: { xs: 3, sm: 6 },
          padding: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
            alt="Chicago Jets Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
            alt="CSDHL Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
        </Stack>
        
        <Typography 
          component="h1" 
          variant="h5" 
          textAlign="center" 
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, mb: 2 }}
        >
          Sign in to Chicago Jets 2014
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        {/* Google Sign-In Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleAuth}
          disabled={loading}
          sx={{
            mt: 0.5,
            mb: 1.5,
            backgroundColor: '#f6f8fa',
            color: '#24292f',
            borderColor: 'rgba(27, 31, 36, 0.15)',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            padding: '8px 16px',
            borderRadius: '6px',
            boxShadow: '0 1px 0 rgba(27,31,36,0.04), inset 0 1px 0 hsla(0,0%,100%,.25)',
            '&:hover': {
              backgroundColor: '#f3f4f6',
              borderColor: 'rgba(27, 31, 36, 0.15)',
            },
          }}
        >
          Continue with Google
        </Button>

        <Divider sx={{ width: '100%', my: 1.5 }}>
          <Typography variant="caption" color="text.secondary">or enter email to sign in</Typography>
        </Divider>

        {/* Passwordless Email Sign In Form */}
        <Box component="form" onSubmit={handlePasswordlessSignIn} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="signin-email"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="small"
            placeholder="name@example.com"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            startIcon={<MailOutlineIcon />}
            sx={{ 
              mt: 1.5, 
              mb: 1, 
              py: 1, 
              textTransform: 'none', 
              fontWeight: 600,
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Sign-In Link'}
          </Button>
        </Box>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Link to Password Sign-In */}
        <Box textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Prefer using a password?{' '}
            <Link 
              component={RouterLink} 
              to="/password-signin" 
              sx={{ fontWeight: 500 }}
            >
              Sign in with password
            </Link>
          </Typography>
        </Box>
      </Paper>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default LoginPage;
